import { useEffect, useMemo, useState } from "react";
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  Toolbar,
  ToolbarButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Chip, Tooltip } from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Search from "assets/icons/search.gif";
import { plGridLocales } from "utils/muiGridLocales";
import {
  buildFromCsv,
  loadJournalFromStorage,
  formatKwota,
  isPostingOrder,
  JOURNALS_ROWS_KEY,
  CsvPayload,
  JournalData,
  JournalRow,
  MOCK_DATA,
} from "utils/journalDocuments";
import { useJournal } from "api/accountingService";
import DocTypeSelect from "components/Registry/DocTypeSelect";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Brak poleceń księgowania (PK) w dzienniku.</div>
  </div>
);

const PostingOrdersRegister: FC<Props> = ({ searchText, setSubtitle, docType, onDocTypeChange }) => {
  const apiRef = useGridApiRef();

  const [data, setData] = useState<JournalData>(() => loadJournalFromStorage() ?? MOCK_DATA);

  // Hydratacja z BE, gdy brak danych lokalnych (analogicznie do „Dziennik księgowań").
  const { data: beJournal } = useJournal();
  useEffect(() => {
    if (!beJournal) return;
    if (localStorage.getItem(JOURNALS_ROWS_KEY)) return;
    try {
      const headers = JSON.parse(beJournal.headersJson) as string[];
      const rows = JSON.parse(beJournal.rowsJson) as Record<string, unknown>[];
      if (!headers.length) return;
      const payload: CsvPayload = {
        v: 2, headers, rows,
        importedAt: Date.parse(beJournal.importedAt) || Date.now(),
        fileName: beJournal.fileName ?? "",
        systemId: beJournal.systemId,
      };
      setData(buildFromCsv(payload));
    } catch { /* ignore */ }
  }, [beJournal]);

  // Odbiór świeżego importu z MigrationTab
  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<CsvPayload>).detail;
      if (!payload?.headers) return;
      setData(buildFromCsv(payload));
    };
    window.addEventListener("journals_updated", handler);
    return () => window.removeEventListener("journals_updated", handler);
  }, []);

  // Tylko dokumenty PK (polecenia księgowania), opcjonalnie filtrowane tekstem.
  const rows = useMemo(() => {
    const q = (searchText ?? "").trim().toLowerCase();
    return data.docRows
      .filter((r) => isPostingOrder(r.typDokumentu))
      .filter((r) => !q ||
        `${r.nrDokumentu} ${r.opis} ${r.kontrahent ?? ""}`.toLowerCase().includes(q))
      .map((r) => ({ ...r }));
  }, [data.docRows, searchText]);

  useEffect(() => {
    setSubtitle(`Polecenia księgowania · ${rows.length} ${rows.length === 1 ? "dokument" : "dokumentów"}`);
  }, [rows.length, setSubtitle]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "__lp",
      headerName: "Lp.",
      width: 52,
      sortable: false,
      filterable: false,
      groupable: false,
      disableColumnMenu: true,
      renderHeader: () => <div className={styles.header}>Lp.</div>,
      renderCell: (params: GridRenderCellParams) => {
        const idx = rows.findIndex((r) => r.id === (params.row as JournalRow).id) + 1;
        return <span className={styles.lpBadge}>{idx}</span>;
      },
    },
    {
      field: "typDokumentu",
      headerName: "Typ",
      width: 80,
      sortable: false,
      renderHeader: () => <div className={styles.header}>Typ</div>,
      renderCell: () => (
        <Chip
          label="PK"
          size="small"
          sx={{ fontWeight: 700, fontSize: 11, color: "#5d49c7", background: "rgba(93,73,199,0.1)", border: "1px solid rgba(93,73,199,0.2)", height: 20 }}
        />
      ),
    },
    {
      field: "nrDokumentu",
      headerName: "Nr dokumentu",
      width: 190,
      renderHeader: () => <div className={styles.header}>Nr dokumentu</div>,
      renderCell: (params: GridRenderCellParams) => (
        <div className={styles.cell} title={params.value as string}>{params.value}</div>
      ),
    },
    {
      field: "dataKsiegowania",
      headerName: "Data ks.",
      width: 110,
      renderHeader: () => <div className={styles.header}>Data ks.</div>,
      valueFormatter: (v: string) => {
        if (!v) return "";
        const [y, m, d] = v.split("-");
        return `${d}.${m}.${y}`;
      },
    },
    {
      field: "opis",
      headerName: "Treść",
      flex: 1,
      minWidth: 220,
      renderHeader: () => <div className={styles.header}>Treść</div>,
      renderCell: (params: GridRenderCellParams) => (
        <div className={styles.cell} title={params.value as string}>{params.value}</div>
      ),
    },
    {
      field: "kontrahent",
      headerName: "Kontrahent",
      width: 200,
      sortable: false,
      renderHeader: () => <div className={styles.header}>Kontrahent</div>,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? <div className={styles.cell} title={params.value as string} style={{ color: "#5d49c7" }}>{params.value}</div> : null,
    },
    {
      field: "kwota",
      headerName: "Kwota",
      width: 140,
      type: "number",
      renderHeader: () => <div className={styles.header}>Kwota</div>,
      renderCell: (params: GridRenderCellParams) => {
        const v = params.value as number | null;
        if (v == null) return null;
        return <span className={styles.kwota}>{formatKwota(v)}</span>;
      },
    },
  ], [rows]);

  const PostingOrdersToolbar = () => (
    <Toolbar>
      <div className={styles.mergedToolbar}>
        <div className={styles.spacer} />
        <div className={styles.toolbarRight}>
          <DocTypeSelect value={docType} onChange={onDocTypeChange} />
          <Tooltip title="Kolumny" placement="bottom-end">
            <ColumnsPanelTrigger render={<ToolbarButton sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: "4px", padding: "4px 8px", color: "#5f5c71", minWidth: "unset", "&:hover": { borderColor: "#5d49c7", background: "rgba(93,73,199,0.04)", color: "#5d49c7" } }} />}>
              <ViewColumnIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>
        </div>
      </div>
    </Toolbar>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          localeText={plGridLocales}
          slots={{ toolbar: PostingOrdersToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          disableRowSelectionOnClick
          columnHeaderHeight={30}
          columns={columns}
          rows={rows}
          hideFooter
          disableColumnFilter
          disablePivoting
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
          }}
        />
      </div>
    </div>
  );
};

export default PostingOrdersRegister;
